import React, { useContext, useMemo } from "react";
import { Change, Variant } from "@bloomberg/media-ui-markets";
import Link from "next/link";
import styles from "./style.module.css";
import { QuoteContext } from "~context/QuoteContext";
import ScrollableTable from "~components/ScrollableTable";
import { IndexMember, TableColumnDefinition } from "~types/types";
import IndexMovers from "~components/QuoteMembers/IndexMovers";
import { LocalizedText } from "~utils/i18n/translate";
import { formatBigNumber } from "~utils/portfolio/tickerUtils";
import { getEasternTimeZone } from "~utils/section-front/getEasternTimeZone";
import { useLocale } from "~hooks/i18n/useLocale";
import timeFormat from "~utils/timeFormat";
import { Locale } from "~constants/i18n";

const renderMember = (
  member: IndexMember,
  _: TableColumnDefinition<IndexMember>
) => {
  const { id, name } = member;
  const stockUrl = `/quote/${id}`;

  return (
    <div className={styles.memberCell}>
      <Link className={styles.link} href={stockUrl} target="_blank">
        <div className={styles.memberId}>{id}</div>
        <div className={styles.memberName}>{name}</div>
      </Link>
    </div>
  );
};

const renderPriceChange = (
  member: IndexMember,
  column: TableColumnDefinition<IndexMember>
) => {
  const rawValue = member[column.key] as number;

  if (column.options?.hidePercentChange) {
    return (
      <Change
        percentChange={rawValue}
        priceChange={rawValue}
        hidePercentChange
        variant={Variant.refresh2024}
      />
    );
  }

  return <Change percentChange={rawValue} variant={Variant.refresh2024} />;
};

const renderVolume = (
  member: IndexMember,
  column: TableColumnDefinition<IndexMember>
) => {
  const locale = (column.options?.locale as "ja") || "en";
  const volume = formatBigNumber(member.volume, 3, true, locale);

  return <span className={styles.memberValueCell}>{volume}</span>;
};

const renderTime = (
  member: IndexMember,
  column: TableColumnDefinition<IndexMember>
) => {
  const locale = (column.options?.locale as Locale.Ja) || Locale.En;
  const time = timeFormat(
    member.lastUpdate,
    locale,
    locale === "ja" ? "hh:mm" : "h:mm a"
  );

  return <span className={styles.memberValueCell}>{time}</span>;
};

const getColumns = (locale: string) => {
  const timeZone = locale === "ja" ? "JST" : getEasternTimeZone();

  const columns: TableColumnDefinition<IndexMember>[] = [
    {
      title: { id: "member", defaultMessage: "Member" },
      key: "id",
      align: "start",
      renderer: renderMember,
      allowSorting: true,
      options: {
        locale,
      },
    },
    {
      title: { id: "price", defaultMessage: "Price" },
      key: "price",
      align: "end",
      allowSorting: true,
      className: styles.memberValueCell,
      options: {
        locale,
      },
    },
    {
      title: { id: "change", defaultMessage: "Change" },
      key: "priceChange1Day",
      align: "end",
      allowSorting: true,
      options: {
        hidePercentChange: true,
        locale,
      },
      renderer: renderPriceChange,
    },
    {
      title: { id: "percentChange", defaultMessage: "% Change" },
      key: "percentChange1Day",
      align: "end",
      allowSorting: true,
      renderer: renderPriceChange,
      options: {
        locale,
      },
    },
    {
      title: { id: "volume", defaultMessage: "Volume" },
      key: "volume",
      align: "end",
      allowSorting: true,
      renderer: renderVolume,
      options: {
        locale,
      },
    },
    {
      title: {
        id: "timeJstEst",
        defaultMessage: `Time (${timeZone})`,
      },
      key: "lastUpdate",
      align: "end",
      allowSorting: true,
      renderer: renderTime,
      options: {
        locale,
      },
    },
  ];

  return columns;
};

export default function QuoteMembers() {
  const { locale } = useLocale();
  const quoteData = useContext(QuoteContext);

  const columns = useMemo(() => {
    return getColumns(locale);
  }, [locale]);

  if (!quoteData?.indexMembers) {
    return null;
  }

  return (
    <div className={styles.members}>
      <div className={styles.titleWrapper}>
        <h2 className={styles.title}>
          <LocalizedText
            value={{ id: "overview", defaultMessage: "Overview" }}
          />
        </h2>
      </div>
      <IndexMovers indexMembers={quoteData.indexMembers} />
      <ScrollableTable
        className={styles.table}
        rows={quoteData.indexMembers}
        columns={columns}
      />
    </div>
  );
}
